import { of } from 'rxjs';
import type { QuippyBuildingSession, QuippyScope } from '../models';
import { documentActionContext } from '../pack-actions/source-context';
import type { QuippyApi } from '../transport';
import { processAssistantRequest$ } from './assistant-response';
import {
    buildConversationHistory,
    createLoadingAssistantMessage,
    createUserMessage,
    findRetryContext,
    findThread,
} from './helpers';
import {
    restoreThreadInSession,
    retryAssistantMessageInSession,
} from './request-state';
import { buildPostModel } from './scope';
import {
    addAssistantMessageToSession,
    addUserMessageToSession,
    resendEditedUserMessageInSession,
} from './state';

interface MessageProcessingOptions {
    api: QuippyApi;
    scope: QuippyScope;
    chatId: string;
    getSession: (scope: QuippyScope) => QuippyBuildingSession;
    updateSession: (
        scope: QuippyScope,
        updater: (session: QuippyBuildingSession) => QuippyBuildingSession
    ) => void;
}

export function processSendMessage$(
    options: MessageProcessingOptions & {
        userInput: string;
        adminMode: boolean;
    }
) {
    const thread = findThread(
        options.getSession(options.scope),
        options.chatId
    );
    if (!thread) return of(undefined);

    const history = buildConversationHistory(thread.messages);
    const userMessage = createUserMessage(options.userInput);
    options.updateSession(options.scope, (session) =>
        addUserMessageToSession(session, options.chatId, userMessage)
    );
    const pendingMessage = createLoadingAssistantMessage();
    options.updateSession(options.scope, (session) =>
        addAssistantMessageToSession(session, options.chatId, pendingMessage)
    );
    const payload = buildPostModel({
        scope: options.scope,
        buildingContext: options.getSession(options.scope).buildingContext,
        userInput: options.userInput,
        conversationHistory: history,
        explicitRoute: options.adminMode ? 'admin-agent' : undefined,
    });

    if (!options.adminMode && options.api.packActionsEnabled?.())
        payload.documentActionContext = documentActionContext(
            thread.messages,
            options.scope
        );

    return processAssistantRequest$({
        api: options.api,
        scope: options.scope,
        chatId: options.chatId,
        messageId: pendingMessage.id,
        payload,
        retryInput: options.userInput,
        updateSession: options.updateSession,
    });
}

export function processRetryMessage$(
    options: MessageProcessingOptions & {
        messageId: string;
        adminMode: boolean;
    }
) {
    const thread = findThread(
        options.getSession(options.scope),
        options.chatId
    );
    if (!thread) return of(undefined);

    const retryContext = findRetryContext(thread, options.messageId);
    if (!retryContext) return of(undefined);
    options.updateSession(options.scope, (session) =>
        retryAssistantMessageInSession(
            session,
            options.chatId,
            options.messageId,
            {
                content: '',
                status: 'pending',
                sources: [],
                retryInput: retryContext.userInput,
                retrySelectedFileId: retryContext.selectedFileId,
            }
        )
    );
    const payload = buildPostModel({
        scope: options.scope,
        buildingContext: options.getSession(options.scope).buildingContext,
        userInput: retryContext.userInput,
        conversationHistory: retryContext.conversationHistory,
        selectedFileId: retryContext.selectedFileId,
        explicitRoute: options.adminMode ? 'admin-agent' : undefined,
    });

    if (!options.adminMode && options.api.packActionsEnabled?.())
        payload.documentActionContext = documentActionContext(
            thread.messages.slice(
                0,
                thread.messages.findIndex(
                    (message) => message.id === options.messageId
                )
            ),
            options.scope
        );

    return processAssistantRequest$({
        api: options.api,
        scope: options.scope,
        chatId: options.chatId,
        messageId: options.messageId,
        payload,
        retryInput: retryContext.userInput,
        documentSelectionMessageId: retryContext.documentSelectionMessageId,
        onError: () =>
            options.updateSession(options.scope, (session) =>
                restoreThreadInSession(session, options.chatId, thread)
            ),
        updateSession: options.updateSession,
    });
}

export function processEditResendMessage$(
    options: MessageProcessingOptions & {
        messageId: string;
        userInput: string;
        adminMode: boolean;
    }
) {
    const editedInput = options.userInput.trim();
    if (!editedInput) return of(undefined);

    const thread = findThread(
        options.getSession(options.scope),
        options.chatId
    );
    if (!thread) return of(undefined);

    const messageIndex = thread.messages.findIndex(
        (message) => message.id === options.messageId
    );
    const message = thread.messages[messageIndex];
    if (message?.role !== 'user') return of(undefined);

    const conversationHistory = buildConversationHistory(
        thread.messages.slice(0, messageIndex)
    );
    const pendingMessage = createLoadingAssistantMessage();
    options.updateSession(options.scope, (session) =>
        resendEditedUserMessageInSession(
            session,
            options.chatId,
            options.messageId,
            editedInput,
            pendingMessage
        )
    );
    const payload = buildPostModel({
        scope: options.scope,
        buildingContext: options.getSession(options.scope).buildingContext,
        userInput: editedInput,
        conversationHistory,
        explicitRoute: options.adminMode ? 'admin-agent' : undefined,
    });

    if (!options.adminMode && options.api.packActionsEnabled?.())
        payload.documentActionContext = documentActionContext(
            thread.messages.slice(0, messageIndex),
            options.scope
        );

    return processAssistantRequest$({
        api: options.api,
        scope: options.scope,
        chatId: options.chatId,
        messageId: pendingMessage.id,
        payload,
        retryInput: editedInput,
        onError: () =>
            options.updateSession(options.scope, (session) =>
                restoreThreadInSession(session, options.chatId, thread)
            ),
        updateSession: options.updateSession,
    });
}
